// Nutrition log API hooks. Mirrors the shape of useDiet.ts — one hook per
// endpoint surface, mutations always invalidate the relevant cache key on
// success so the dashboard daily-mission card refreshes without a manual
// refetch.
import type {
  ClearDayDto,
  ClearDayResponse,
  CreateNutritionLogDto,
  DailyTotals,
  ListNutritionLogsResponse,
  NutritionLog,
  UpdateNutritionLogDto,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

const NUTRITION_LOGS_KEY = ['nutrition-logs'] as const;

interface DailyTotalsArgs {
  date?: string;
  timezone: string;
}

export function useDailyTotalsQuery({ date, timezone }: DailyTotalsArgs) {
  return useQuery<DailyTotals>({
    queryKey: [...NUTRITION_LOGS_KEY, 'daily-totals', date ?? null, timezone],
    queryFn: () => {
      const params = new URLSearchParams({ timezone });
      if (date) params.set('date', date);
      return api<DailyTotals>(`/logs/nutrition/daily-totals?${params.toString()}`);
    },
    // Daily totals are computed read-side via SUM. 30 s of staleness is fine
    // for a dashboard surface; mutations invalidate explicitly.
    staleTime: 30_000,
  });
}

export function useNutritionLogsQuery({ date, timezone }: DailyTotalsArgs) {
  return useQuery<ListNutritionLogsResponse>({
    queryKey: [...NUTRITION_LOGS_KEY, 'list', date ?? null, timezone],
    queryFn: () => {
      const params = new URLSearchParams({ timezone });
      if (date) params.set('date', date);
      return api<ListNutritionLogsResponse>(`/logs/nutrition?${params.toString()}`);
    },
    staleTime: 30_000,
  });
}

export function useCreateNutritionLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNutritionLogDto) =>
      api<NutritionLog>('/logs/nutrition', { method: 'POST', json: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NUTRITION_LOGS_KEY });
      qc.invalidateQueries({ queryKey: ['foods', 'recent'] });
    },
  });
}

export interface PatchNutritionLogInput {
  id: string;
  patch: UpdateNutritionLogDto;
}

export function usePatchNutritionLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: PatchNutritionLogInput) =>
      api<NutritionLog>(`/logs/nutrition/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NUTRITION_LOGS_KEY });
      qc.invalidateQueries({ queryKey: ['foods', 'recent'] });
    },
  });
}

export function useDeleteNutritionLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/logs/nutrition/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NUTRITION_LOGS_KEY });
      qc.invalidateQueries({ queryKey: ['foods', 'recent'] });
    },
  });
}

export function useClearDayMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClearDayDto) =>
      api<ClearDayResponse>('/logs/nutrition/clear-day', {
        method: 'POST',
        json: input,
      }),
    onSuccess: () => {
      // Wider invalidate than strictly needed — daily-totals + the /log
      // list view both live under the same root key, so one bust refreshes
      // both surfaces.
      qc.invalidateQueries({ queryKey: NUTRITION_LOGS_KEY });
      qc.invalidateQueries({ queryKey: ['foods', 'recent'] });
    },
  });
}
